import { useEffect, useState } from 'react';
import type { EnumDef, EnumValue, TypeDef } from '../types/model';
import { createId, normalizeName } from '../utils/modelUtils';

type CreateModalProps = {
  mode: 'type' | 'enum';
  onModeChange: (mode: 'type' | 'enum') => void;
  types: TypeDef[];
  existingTypeNames: string[];
  existingEnumNames: string[];
  onClose: () => void;
  onCreateType: (type: TypeDef) => void;
  onCreateEnum: (entry: EnumDef) => void;
};

export const CreateModal = ({
  mode,
  onModeChange,
  types,
  existingTypeNames,
  existingEnumNames,
  onClose,
  onCreateType,
  onCreateEnum,
}: CreateModalProps) => {
  const [typeName, setTypeName] = useState('');
  const [typeDescription, setTypeDescription] = useState('');
  const [typeParent, setTypeParent] = useState('');
  const [typeIndexed, setTypeIndexed] = useState(false);
  const [enumName, setEnumName] = useState('');
  const [enumDescription, setEnumDescription] = useState('');
  const [enumValues, setEnumValues] = useState<EnumValue[]>([{ id: createId(), label: '' }]);

  useEffect(() => {
    setTypeName('');
    setTypeDescription('');
    setTypeParent('');
    setTypeIndexed(false);
    setEnumName('');
    setEnumDescription('');
    setEnumValues([{ id: createId(), label: '' }]);
  }, [mode]);

  const normalizedTypeNames = new Set(existingTypeNames.map((name) => normalizeName(name)));
  const normalizedEnumNames = new Set(existingEnumNames.map((name) => normalizeName(name)));
  const typeKey = normalizeName(typeName);
  const enumKey = normalizeName(enumName);

  const typeNameValid = typeKey.length > 0 && !normalizedTypeNames.has(typeKey);
  const enumNameValid = enumKey.length > 0 && !normalizedEnumNames.has(enumKey);
  const enumValuesValid = enumValues.length > 0 && enumValues.every((value) => value.label.trim().length > 0);

  const handleCreateType = () => {
    if (!typeNameValid) {
      return;
    }
    onCreateType({
      id: createId(),
      name: typeName.trim(),
      description: typeDescription.trim(),
      parentTypeId: typeParent ? typeParent : null,
      fields: [],
      isIndexed: typeIndexed,
    });
    onClose();
  };

  const handleCreateEnum = () => {
    if (!enumNameValid || !enumValuesValid) {
      return;
    }
    onCreateEnum({
      id: createId(),
      name: enumName.trim(),
      description: enumDescription.trim(),
      values: enumValues.map((value) => ({ ...value, label: value.label.trim() })),
    });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="panel-title">Create new {mode === 'type' ? 'type' : 'enum'}</div>
          <button type="button" className="button ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-tabs">
          <button
            type="button"
            className={`tab ${mode === 'type' ? 'active' : ''}`}
            onClick={() => onModeChange('type')}
          >
            Create Type
          </button>
          <button
            type="button"
            className={`tab ${mode === 'enum' ? 'active' : ''}`}
            onClick={() => onModeChange('enum')}
          >
            Create Enum
          </button>
        </div>
        <div className="modal-body">
          {mode === 'type' && (
            <>
              <div className="field-column">
                <label className="label">Name</label>
                <input
                  className="input"
                  type="text"
                  value={typeName}
                  onChange={(event) => setTypeName(event.target.value)}
                  placeholder="e.g. Customer"
                />
                {!typeNameValid && typeName.trim() && <div className="warning-text">Name must be unique.</div>}
              </div>
              <div className="field-column">
                <label className="label">Description</label>
                <textarea
                  className="input"
                  rows={3}
                  value={typeDescription}
                  onChange={(event) => setTypeDescription(event.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="field-column">
                <label className="label">Parent type</label>
                <select className="input" value={typeParent} onChange={(event) => setTypeParent(event.target.value)}>
                  <option value="">None</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name || 'Unnamed type'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-column">
                <label className="label">Indexed</label>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={typeIndexed}
                    onChange={(event) => setTypeIndexed(event.target.checked)}
                  />
                  <span>{typeIndexed ? 'Indexed' : 'Not indexed'}</span>
                </label>
              </div>
            </>
          )}
          {mode === 'enum' && (
            <>
              <div className="field-column">
                <label className="label">Name</label>
                <input
                  className="input"
                  type="text"
                  value={enumName}
                  onChange={(event) => setEnumName(event.target.value)}
                  placeholder="e.g. Priority"
                />
                {!enumNameValid && enumName.trim() && <div className="warning-text">Name must be unique.</div>}
              </div>
              <div className="field-column">
                <label className="label">Description</label>
                <textarea
                  className="input"
                  rows={3}
                  value={enumDescription}
                  onChange={(event) => setEnumDescription(event.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="field-column">
                <label className="label">Values</label>
                <div className="enum-values">
                  {enumValues.map((value) => (
                    <div key={value.id} className="enum-row">
                      <input
                        className="input"
                        type="text"
                        value={value.label}
                        onChange={(event) =>
                          setEnumValues((prev) =>
                            prev.map((item) => (item.id === value.id ? { ...item, label: event.target.value } : item))
                          )
                        }
                        placeholder="Label"
                      />
                      <button
                        type="button"
                        className="button danger"
                        onClick={() => setEnumValues((prev) => prev.filter((item) => item.id !== value.id))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                {!enumValuesValid && <div className="warning-text">All enum values must have labels.</div>}
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => setEnumValues((prev) => [...prev, { id: createId(), label: '' }])}
                >
                  Add value
                </button>
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="button ghost" onClick={onClose}>
            Cancel
          </button>
          {mode === 'type' ? (
            <button type="button" className="button primary" onClick={handleCreateType} disabled={!typeNameValid}>
              Create Type
            </button>
          ) : (
            <button
              type="button"
              className="button primary"
              onClick={handleCreateEnum}
              disabled={!enumNameValid || !enumValuesValid}
            >
              Create Enum
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
